/**
 * SocialDynamicsPanel.jsx - Social dynamics visualization component
 * Displays opinion evolution, convergence, and polarization metrics
 * Extracted from Analysis.jsx
 */
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Network } from 'lucide-react';
import { cn } from '../../lib/utils';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

const SocialDynamicsPanel = ({ data }) => {
  if (!data || !data.social_enabled) return null;
  
  const { 
    stats_per_round = [], 
    social_config = {}, 
    convergence_round, 
    graph_info = {}, 
    round_distributions = [], 
    response_scale = [],
    social_diagnostics = {}
  } = data;
  
  // Opinion Evolution data for line chart
  const evolutionData = stats_per_round.map(s => ({
    round: `R${s.round}`,
    mean: +(s.mean * 100).toFixed(1),
    std: +(s.std * 100).toFixed(1),
    min: +(s.min * 100).toFixed(1),
    max: +(s.max * 100).toFixed(1),
  }));

  // Convergence data
  const convergenceData = stats_per_round.map(s => ({
    round: `R${s.round}`,
    pct_changed: +s.pct_changed.toFixed(1),
    avg_delta: +(s.avg_delta * 1000).toFixed(2),
  }));

  // Polarization data
  const polarizationData = stats_per_round.map(s => ({
    round: `R${s.round}`,
    std: +(s.std * 100).toFixed(2),
  }));

  const lastStats = stats_per_round[stats_per_round.length - 1] || {};
  const firstStats = stats_per_round[0] || {};
  const meanShift = lastStats.mean !== undefined && firstStats.mean !== undefined
    ? ((lastStats.mean - firstStats.mean) * 100).toFixed(1) : '0';
  const finalPolarization = lastStats.std !== undefined ? (lastStats.std * 100).toFixed(1) : '0';
  const initialPolarization = firstStats.std !== undefined ? (firstStats.std * 100).toFixed(1) : '0';
  const polarizationDelta = (parseFloat(finalPolarization) - parseFloat(initialPolarization)).toFixed(1);

  const diagnosticsPost = social_diagnostics.post || {};
  const diagnosticsDelta = social_diagnostics.delta || {};

  const formatNetworkType = (value = '') => value.replace(/_/g, ' ');
  
  // Generate user-friendly interpretive summary
  const getSummary = () => {
    const parts = [];
    const shift = parseFloat(meanShift);
    const polDelta = parseFloat(polarizationDelta);
    
    if (Math.abs(shift) < 2) {
      parts.push("Overall opinions stayed stable during social interactions");
    } else if (shift > 0) {
      parts.push(`Opinions shifted more positive by ${Math.abs(shift).toFixed(0)} points`);
    } else {
      parts.push(`Opinions shifted more negative by ${Math.abs(shift).toFixed(0)} points`);
    }
    
    if (polDelta < -3) {
      parts.push("People became more unified (reached more consensus)");
    } else if (polDelta > 3) {
      parts.push("People became more divided (opinions spread apart)");
    } else {
      parts.push("Opinion diversity remained roughly the same");
    }
    
    return parts.join(". ") + ".";
  };

  return (
    <div className="space-y-6" data-testid="social-dynamics-panel">
      {/* Summary Card - Plain English */}
      <Card className="bg-gradient-to-r from-blue-50 to-blue-50 border-blue-200">
        <CardHeader className="pb-2">
          <div className="flex items-center gap-2">
            <Network className="w-5 h-5 text-blue-600" />
            <CardTitle className="text-lg">Social Influence Summary</CardTitle>
            {convergence_round && (
              <Badge className="bg-positive-soft text-positive-soft-foreground border-positive-border">
                Consensus at Round {convergence_round}
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-slate-700 dark:text-slate-300 leading-relaxed">
            {getSummary()}
          </p>
          
          {/* Key Metrics - Simple Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
            <div className="bg-white dark:bg-card rounded-lg p-3 border border-blue-100 dark:border-border">
              <p className="text-xs text-slate-500 uppercase tracking-wide">Rounds Run</p>
              <p className="text-2xl font-bold text-slate-800 dark:text-slate-100">{social_config.rounds || 0}</p>
              <p className="text-[10px] text-slate-400">interactions</p>
            </div>
            <div className="bg-white dark:bg-card rounded-lg p-3 border border-blue-100 dark:border-border">
              <p className="text-xs text-slate-500 uppercase tracking-wide">Opinion Shift</p>
              <p className={cn(
                "text-2xl font-bold",
                parseFloat(meanShift) > 2 ? "text-positive-soft-foreground" : parseFloat(meanShift) < -2 ? "text-negative-soft-foreground" : "text-slate-600"
              )}>
                {parseFloat(meanShift) > 0 ? '+' : ''}{meanShift}
              </p>
              <p className="text-[10px] text-slate-400">points change</p>
            </div>
            <div className="bg-white dark:bg-card rounded-lg p-3 border border-blue-100 dark:border-border">
              <p className="text-xs text-slate-500 uppercase tracking-wide">Unity Change</p>
              <p className={cn(
                "text-2xl font-bold",
                parseFloat(polarizationDelta) < -2 ? "text-positive-soft-foreground" : parseFloat(polarizationDelta) > 2 ? "text-warning-soft-foreground" : "text-slate-600"
              )}>
                {parseFloat(polarizationDelta) < 0 ? '↑' : parseFloat(polarizationDelta) > 0 ? '↓' : '→'}
              </p>
              <p className="text-[10px] text-slate-400">{parseFloat(polarizationDelta) < -2 ? 'more unified' : parseFloat(polarizationDelta) > 2 ? 'more divided' : 'stable'}</p>
            </div>
            <div className="bg-white dark:bg-card rounded-lg p-3 border border-blue-100 dark:border-border">
              <p className="text-xs text-slate-500 uppercase tracking-wide">Network</p>
              <p className="text-lg font-bold text-slate-800 dark:text-slate-100 capitalize">{formatNetworkType(social_config.network_type || '')}</p>
              <p className="text-[10px] text-slate-400">{graph_info.node_count} people, {graph_info.edge_count} connections</p>
            </div>
          </div>

          {/* Diagnostics */}
          {social_diagnostics.pre && (
            <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="social-diagnostics">
              <div className="bg-white dark:bg-card rounded-lg p-3 border border-blue-100 dark:border-border">
                <p className="text-xs text-slate-500 uppercase tracking-wide">Entropy</p>
                <p className="text-lg font-semibold text-slate-800 dark:text-slate-100">{diagnosticsPost.entropy ?? 0}</p>
                <p className="text-[10px] text-slate-400">Δ {diagnosticsDelta.entropy ?? 0}</p>
              </div>
              <div className="bg-white dark:bg-card rounded-lg p-3 border border-blue-100 dark:border-border">
                <p className="text-xs text-slate-500 uppercase tracking-wide">Variance</p>
                <p className="text-lg font-semibold text-slate-800 dark:text-slate-100">{diagnosticsPost.variance ?? 0}</p>
                <p className="text-[10px] text-slate-400">Δ {diagnosticsDelta.variance ?? 0}</p>
              </div>
              <div className="bg-white dark:bg-card rounded-lg p-3 border border-blue-100 dark:border-border">
                <p className="text-xs text-slate-500 uppercase tracking-wide">Tail Share</p>
                <p className="text-lg font-semibold text-slate-800 dark:text-slate-100">{(diagnosticsPost.polarization_index ?? 0).toFixed(2)}</p>
                <p className="text-[10px] text-slate-400">Δ {(diagnosticsDelta.polarization_index ?? 0).toFixed(2)}</p>
              </div>
              <div className="bg-white dark:bg-card rounded-lg p-3 border border-blue-100 dark:border-border">
                <p className="text-xs text-slate-500 uppercase tracking-wide">Peaks</p>
                <p className="text-lg font-semibold text-slate-800 dark:text-slate-100">{diagnosticsPost.peak_count ?? 0}</p>
                <p className="text-[10px] text-slate-400">Δ {diagnosticsDelta.peak_count ?? 0}</p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Opinion Evolution Timeline */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              Opinion Trend
              <Badge variant="outline" className="text-[10px] font-normal">Higher = More Positive</Badge>
            </CardTitle>
            <p className="text-xs text-muted-foreground">How the average opinion changed over interaction rounds</p>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart data={evolutionData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="round" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} domain={[-100, 100]} unit="%" />
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                <Area type="monotone" dataKey="max" stroke="#a78bfa" fill="#ede9fe" fillOpacity={0.4} strokeWidth={1} strokeDasharray="4 2" name="Max" />
                <Area type="monotone" dataKey="min" stroke="#a78bfa" fill="#ede9fe" fillOpacity={0.4} strokeWidth={1} strokeDasharray="4 2" name="Min" />
                <Line type="monotone" dataKey="mean" stroke="#6366f1" strokeWidth={2.5} dot={{ r: 4, fill: '#6366f1' }} name="Mean Score" />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Convergence Metric - Simplified */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              Opinion Changes Per Round
              <Badge variant="outline" className="text-[10px] font-normal">Lower = More Stable</Badge>
            </CardTitle>
            <p className="text-xs text-muted-foreground">How many people changed their opinion in each round</p>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={convergenceData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="round" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} unit="%" />
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                <Bar dataKey="pct_changed" fill="#8b5cf6" radius={[4, 4, 0, 0]} name="% Changed Opinion" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Opinion Spread (was Polarization) */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              Opinion Spread
              <Badge variant="outline" className="text-[10px] font-normal">Lower = More Agreement</Badge>
            </CardTitle>
            <p className="text-xs text-muted-foreground">How spread out opinions are (standard deviation)</p>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={polarizationData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="round" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} unit="%" />
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                <Line type="monotone" dataKey="std" stroke="#f59e0b" strokeWidth={2} dot={{ r: 4, fill: '#f59e0b' }} name="Opinion Spread" />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Before & After Distribution */}
        {round_distributions.length > 0 && response_scale.length > 0 && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Before & After Social Interaction</CardTitle>
              <p className="text-xs text-muted-foreground">Compare initial responses (light) vs final responses (dark)</p>
            </CardHeader>
            <CardContent>
              {(() => {
                const first = round_distributions[0]?.distribution || {};
                const last = round_distributions[round_distributions.length - 1]?.distribution || {};
                const chartData = response_scale.map(opt => ({
                  name: opt,
                  'Before': first[opt] || 0,
                  'After': last[opt] || 0,
                }));
                return (
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                      <Bar dataKey="Before" fill="#c4b5fd" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="After" fill="#6366f1" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                );
              })()}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default SocialDynamicsPanel;
